import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ScanLine, Users, CheckCircle2, ExternalLink, Trash2, Archive, RotateCcw } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { DeleteEventDialog } from "@/components/DeleteEventDialog";
import { useAuth } from "@/context/AuthContext";

const blank = {
  name: "",
  date: "",
  start_time: "09:00",
  end_time: "17:00",
  venue: "",
  description: "",
  key_visual_url: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1600&q=80",
  capacity: 100,
  slug: "",
  waitlist_enabled: true,
  status: "published",
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});

  useEffect(() => {
    if (!loading && !user) navigate("/moderator/login");
    if (user && user.role !== "staff") load();
  }, [user, loading, navigate]);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: k === "capacity" ? Number(e.target.value) : e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/events", form);
      toast.success("Event created — share the link to open registrations.");
      setShowForm(false);
      setForm(blank);
      navigate(`/moderator/events/${data.event_id}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (password) => {
    try {
      await api.delete(`/events/${confirmDelete.event_id}`, { data: { password } });
      toast.success("Event deleted");
      load();
      return true;
    } catch (err) {
      toast.error(errText(err));
      return false;
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/events/${id}/status`, { status });
      toast.success(status === "completed" ? "Event closed" : "Event reopened");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (loading) return <Shell><p className="text-sm text-slate-500">Loading…</p></Shell>;

  if (user?.role === "staff")
    return (
      <Shell wide>
        <div data-testid="staff-dashboard">
          <p className="overline">Check-in staff</p>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight mt-2">Scanner stations</h1>
          <p className="text-sm text-slate-400 mt-4">
            Ask your moderator for the event scanner link, or open a station below.
          </p>
          <Link to="/scanner" className="btn-primary mt-6" data-testid="staff-open-scanner-btn">
            <ScanLine size={15} /> Open scanner
          </Link>
        </div>
      </Shell>
    );

  return (
    <Shell wide>
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="dashboard-page">
        <div>
          <p className="overline">Command center</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
            My events
          </h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="new-event-btn">
          <Plus size={16} /> New event
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card p-6 mt-8 rise" data-testid="event-form">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Create event</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="sm:col-span-2">
              <label className="label">Event name</label>
              <input required className="input" data-testid="event-name-input" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className="label">Date</label>
              <input required type="date" className="input" data-testid="event-date-input" value={form.date} onChange={set("date")} />
            </div>
            <div>
              <label className="label">Custom link slug (optional)</label>
              <input className="input" placeholder="ark-summit-2026" data-testid="event-slug-input" value={form.slug} onChange={set("slug")} />
            </div>
            <div>
              <label className="label">Start time</label>
              <input type="time" className="input" data-testid="event-start-input" value={form.start_time} onChange={set("start_time")} />
            </div>
            <div>
              <label className="label">End time</label>
              <input type="time" className="input" data-testid="event-end-input" value={form.end_time} onChange={set("end_time")} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Venue</label>
              <input className="input" data-testid="event-venue-input" value={form.venue} onChange={set("venue")} />
            </div>
            <div className="sm:col-span-2">
              <p className="font-mono2 text-[11px] text-slate-500">
                You can add the event image once it's created — open Edit details on the event.
              </p>
            </div>
            <div>
              <label className="label">Capacity</label>
              <input type="number" min="1" className="input" data-testid="event-capacity-input" value={form.capacity} onChange={set("capacity")} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  className="accent-[#00e599] w-4 h-4"
                  data-testid="event-waitlist-toggle"
                  checked={form.waitlist_enabled}
                  onChange={(e) => setForm({ ...form, waitlist_enabled: e.target.checked })}
                />
                Enable waitlist when full
              </label>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Description</label>
              <textarea rows="4" className="input" data-testid="event-description-input" value={form.description} onChange={set("description")} />
            </div>
          </div>
          <button className="btn-primary mt-6" disabled={busy} data-testid="event-submit-btn">
            {busy ? "Creating…" : "Create event"}
          </button>
        </form>
      )}

      {events.length === 0 ? (
        <p className="text-sm text-slate-500 mt-10" data-testid="dashboard-empty">
          No events yet — create your first one.
        </p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2 mt-10" data-testid="dashboard-event-list">
          {events.map((e) => (
            <div key={e.event_id} className="card p-5 hover:border-[#00e599]/40 transition-colors" data-testid={`dashboard-event-${e.event_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono2 text-[11px] uppercase tracking-widest text-[#00e599]">
                    {e.date} · {e.start_time}
                  </p>
                  <h3 className="text-xl font-bold mt-1.5 truncate">{e.name}</h3>
                  <p className="text-xs text-slate-400 mt-1 truncate">{e.venue}</p>
                  {e.status !== "published" && (
                    <span
                      className={`inline-block font-mono2 text-[10px] uppercase tracking-widest mt-2 px-2 py-0.5 rounded-full border ${
                        e.status === "completed"
                          ? "text-slate-400 border-[#38475c]"
                          : "text-[#ffb800] border-[#ffb800]/40"
                      }`}
                      data-testid={`event-status-${e.event_id}`}
                    >
                      {e.status}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setConfirmDelete(e)}
                  className="text-red-400/70 hover:text-red-400"
                  data-testid={`delete-event-${e.event_id}`}
                  title="Delete permanently"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5 font-mono2 text-xs">
                <div className="bg-[#141a23] rounded-lg p-3">
                  <p className="text-slate-500 flex items-center gap-1">
                    <Users size={12} /> Reg
                  </p>
                  <p className="text-lg text-slate-100">{e.registered}</p>
                </div>
                <div className="bg-[#141a23] rounded-lg p-3">
                  <p className="text-slate-500 flex items-center gap-1">
                    <CheckCircle2 size={12} /> In
                  </p>
                  <p className="text-lg text-[#00e599]">{e.checked_in}</p>
                </div>
                <div className="bg-[#141a23] rounded-lg p-3">
                  <p className="text-slate-500">Cap</p>
                  <p className="text-lg text-slate-100">{e.capacity}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-5">
                <Link to={`/moderator/events/${e.event_id}`} className="btn-ghost" data-testid={`manage-event-${e.event_id}`}>
                  Manage
                </Link>
                <Link to={`/scanner/${e.event_id}`} className="btn-ghost" data-testid={`scan-event-${e.event_id}`}>
                  <ScanLine size={14} /> Scanner
                </Link>
                <Link to={`/e/${e.slug}`} className="btn-ghost" data-testid={`public-event-${e.event_id}`}>
                  <ExternalLink size={14} /> Public link
                </Link>
                {e.status === "completed" ? (
                  <button onClick={() => setStatus(e.event_id, "published")} className="btn-ghost" data-testid={`reopen-event-${e.event_id}`}>
                    <RotateCcw size={14} /> Reopen
                  </button>
                ) : (
                  <button onClick={() => setStatus(e.event_id, "completed")} className="btn-ghost" data-testid={`close-event-${e.event_id}`}>
                    <Archive size={14} /> Close event
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <DeleteEventDialog
          event={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={remove}
        />
      )}
    </Shell>
  );
}
